import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  DocumentTextIcon,
  CogIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalConfigurations: 0,
    totalDocumentsProcessed: 0,
    recentActivity: [],
    systemHealth: 'healthy'
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load configurations
      const configResponse = await fetch('/api/v1/yourfabric/configs');
      if (configResponse.ok) {
        const configData = await configResponse.json();
        setStats(prev => ({
          ...prev,
          totalConfigurations: configData.configs?.length || 0
        }));
      }

      // For now, mock other data - in real implementation would come from analytics API
      setStats(prev => ({
        ...prev,
        totalDocumentsProcessed: 1247,
        recentActivity: [
          { type: 'config_created', message: 'New configuration "Healthcare HIPAA" created', timestamp: '2 hours ago' },
          { type: 'classification_run', message: '150 documents classified with "Financial SOX" config', timestamp: '4 hours ago' },
          { type: 'config_tested', message: '"Legal Contracts" configuration tested successfully', timestamp: '6 hours ago' }
        ]
      }));
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">YourFabric Dashboard</h1>
        <p className="text-lg text-gray-600 mt-2">
          Monitor your AI classification systems and performance
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CogIcon className="w-8 h-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.totalConfigurations}</p>
              <p className="text-sm text-gray-600">Active Configurations</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <DocumentTextIcon className="w-8 h-8 text-green-500" />
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.totalDocumentsProcessed}</p>
              <p className="text-sm text-gray-600">Documents Classified</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
            <div className="ml-4">
              <p className="text-lg font-bold text-green-600">Healthy</p>
              <p className="text-sm text-gray-600">System Status</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {stats.recentActivity.map((activity, index) => (
            <div key={index} className="p-6 flex items-start">
              <div className="flex-shrink-0">
                {activity.type === 'config_created' && (
                  <div className="p-2 bg-blue-100 rounded-full">
                    <CogIcon className="w-4 h-4 text-blue-600" />
                  </div>
                )}
                {activity.type === 'classification_run' && (
                  <div className="p-2 bg-green-100 rounded-full">
                    <ChartBarIcon className="w-4 h-4 text-green-600" />
                  </div>
                )}
                {activity.type === 'config_tested' && (
                  <div className="p-2 bg-yellow-100 rounded-full">
                    <DocumentTextIcon className="w-4 h-4 text-yellow-600" />
                  </div>
                )}
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm text-gray-900">{activity.message}</p>
                <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
              </div>
            </div>
          ))}
        </div>

        {stats.recentActivity.length === 0 && (
          <div className="p-12 text-center">
            <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No recent activity</p>
          </div>
        )}
      </div>
    </div>
  );
}